import { Controller } from "@hotwired/stimulus"

// "Add past meeting + transcript" (K3-9, Keane Kloepfer 2026-10-08): the
// form in the Meetings list's pop-up. Takes the transcript file by drop or
// pick (the zone highlights inline), names the meeting after the file and
// reads a date out of its name when there is one, and shows that the
// minutes are being written while the form submits.
export default class extends Controller {
  static targets = ["dropZone", "fileInput", "fileName", "status", "title", "date", "time", "duration", "kind", "attendees", "attendeeList", "submit", "submitLabel"]
  static values = { writing: String, previewUrl: String, messages: Object }

  dragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("border-violet-500", "bg-violet-100")
  }

  dragLeave(event) {
    event.preventDefault()
    if (this.dropZoneTarget.contains(event.relatedTarget)) return
    this.dropZoneTarget.classList.remove("border-violet-500", "bg-violet-100")
  }

  drop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-violet-500", "bg-violet-100")
    const file = event.dataTransfer?.files?.[0]
    if (!file) return
    // The dropped file becomes the input's file, so a plain form submit carries it.
    const list = new DataTransfer()
    list.items.add(file)
    this.fileInputTarget.files = list.files
    this._picked(file)
  }

  fileChosen(event) {
    const file = event.target.files?.[0]
    if (file) this._picked(file)
  }

  _picked(file) {
    this.fileNameTarget.hidden = false
    this.fileNameTarget.textContent = `${file.name} · ${Math.max(1, Math.round(file.size / 1024))} KB`
    this.submitTarget.disabled = false
    if (this.hasTitleTarget && !this.titleTarget.value.trim()) this.titleTarget.value = this._titleFrom(file.name)
    const date = this._dateFrom(file.name)
    if (date) this._setDate(date)
    this._preview(file)
  }

  _setDate(date) {
    if (!this.hasDateTarget) return
    this.dateTarget.value = date
    this.dateTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // The file, read by the server before anything is saved: who spoke
  // (matched to the project's people), how long it ran, and the date, time
  // and title when the words say them. Fills the form for a look-over.
  async _preview(file) {
    if (!this.previewUrlValue) return
    const m = this.messagesValue || {}
    const status = this.statusTarget
    status.hidden = false
    status.classList.remove("text-red-600")
    status.textContent = m.reading || ""
    this.submitTarget.disabled = true
    const body = new FormData()
    body.append("file", file)
    try {
      const res = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "", "Accept": "application/json" },
        body
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || m.failed)
      if (data.title && this.hasTitleTarget && !this.titleTarget.dataset.typed) this.titleTarget.value = data.title
      if (data.date) this._setDate(data.date)
      if (data.start_at && this.hasTimeTarget) this.timeTarget.value = data.start_at.padStart(5, "0")
      if (data.duration && this.hasDurationTarget) this.durationTarget.value = data.duration
      // The type that fits, when it's one of the choices offered.
      if (data.kind && this.hasKindTarget && [...this.kindTarget.options].some((o) => o.value === data.kind)) this.kindTarget.value = data.kind
      this._renderAttendees(data.speakers || [])
      const found = (data.speakers || []).length
      status.textContent = found
        ? (data.duration ? (m.found || "") : (m.found_no_time || "")).replace("%{count}", found).replace("%{minutes}", data.duration || "")
        : (m.none || "")
    } catch (e) {
      status.classList.add("text-red-600")
      status.textContent = e.message || m.failed || ""
    } finally {
      this.submitTarget.disabled = !this.fileInputTarget.files?.length
    }
  }

  _renderAttendees(speakers) {
    if (!this.hasAttendeesTarget) return
    const list = this.attendeeListTarget
    list.innerHTML = ""
    this.attendeesTarget.hidden = speakers.length === 0
    const guestTag = (this.messagesValue || {}).guest || ""
    speakers.forEach((s) => {
      const label = document.createElement("label")
      label.className = s.user_id
        ? "inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-gray-300 bg-white text-[13px] text-gray-800 cursor-pointer"
        : "inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-dashed border-gray-300 bg-gray-50 text-[13px] text-gray-700 cursor-pointer"
      const box = document.createElement("input")
      box.type = "checkbox"
      box.checked = true
      box.className = "h-3.5 w-3.5 rounded border-gray-300 text-brand"
      box.name = s.user_id ? "attendee_user_ids[]" : "guest_names[]"
      box.value = s.user_id ? String(s.user_id) : s.name
      label.appendChild(box)
      label.appendChild(document.createTextNode(s.user_id ? s.user_name : s.name))
      if (!s.user_id && guestTag) {
        const tag = document.createElement("span")
        tag.className = "text-gray-400"
        tag.textContent = `· ${guestTag}`
        label.appendChild(tag)
      }
      list.appendChild(label)
    })
  }

  titleTyped() {
    if (this.hasTitleTarget) this.titleTarget.dataset.typed = this.titleTarget.value.trim() ? "1" : ""
  }

  // "10-06_Meeting_I-84_Thin_Lift-transcript.txt" → "Meeting I-84 Thin Lift"
  _titleFrom(name) {
    return name.replace(/\.[^.]+$/, "")
      .replace(/^\d{1,4}[-_.]\d{1,2}([-_.]\d{1,4})?[\s_-]*/, "")
      .replace(/[\s_-]*transcript(ion)?$/i, "")
      .replace(/_/g, " ").replace(/\s+/g, " ").trim()
  }

  // A date at the start of the name: 2026-10-06, 10-06-2026 or 10-06 (this year).
  _dateFrom(name) {
    const full = name.match(/^(\d{4})[-_.](\d{1,2})[-_.](\d{1,2})/)
    if (full) return `${full[1]}-${full[2].padStart(2, "0")}-${full[3].padStart(2, "0")}`
    const us = name.match(/^(\d{1,2})[-_.](\d{1,2})(?:[-_.](\d{4}))?(?![\d])/)
    if (!us) return null
    const year = us[3] || String(new Date().getFullYear())
    const month = Number(us[1]), day = Number(us[2])
    if (month < 1 || month > 12 || day < 1 || day > 31) return null
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
  }

  submit(event) {
    if (!this.fileInputTarget.files?.length) {
      event.preventDefault()
      this.fileInputTarget.click()
      return
    }
    this.submitTarget.disabled = true
    if (this.hasSubmitLabelTarget && this.writingValue) this.submitLabelTarget.textContent = this.writingValue
  }
}
